import React, { useEffect, useState, useRef } from 'react';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import ReactGA from 'react-ga4';
import ShareIcon from '@mui/icons-material/Share';

interface InvoiceData {
	order: {
		id: number;
		customer_name: string;
		customer_address: string;
		customer_email: string;
		customer_phone: string;
		order_address?: string;
		order_contact?: string;
		date_created: string;
		currency: string;
		variants: {
			id: number;
			name: string;
			quantity: number;
			unit_price_at_purchase: number;
			discount_amount: number;
			total_price: number;
			addons?:
				| {
						id: number;
						name: string;
						quantity: number;
						unit_price_at_purchase: number;
				  }[]
				| null;
			product?: {
				id?: number;
				name: string;
			};
		}[];
	};
	order_price_total: number;
	shop_name: string;
	shop_logo?: string;
	payment_instruction?: string;
	invoice_date: string;
	invoice_number: string;
	payment_status: string;
}

interface InvoiceViewProps {
	hashId: string;
	skipTracking?: boolean;
}

const InvoiceView: React.FC<InvoiceViewProps> = ({ hashId, skipTracking = true }) => {
	const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const invoiceRef = useRef<HTMLDivElement>(null);

	// Reference to track the original favicon
	const [originalFavicon, setOriginalFavicon] = useState<HTMLLinkElement | null>(null);

	// Share invoice link without skiptracking parameter
	const handleShareInvoice = async () => {
		const baseUrl = window.location.origin;
		const shareUrl = `${baseUrl}/invoice?hash=${hashId}`;

		try {
			if (navigator.share) {
				// Use Web Share API if available
				await navigator.share({
					title: `Invoice from ${invoiceData?.shop_name || 'VendReady'}`,
					text: `View invoice #${invoiceData?.invoice_number || ''}`,
					url: shareUrl,
				});
				ReactGA.event({
					category: 'Invoice',
					action: 'Shared Invoice via Web Share API',
					label: `Invoice: ${hashId}`,
				});
			} else {
				// Fallback to clipboard copy
				await navigator.clipboard.writeText(shareUrl);
				notify('success', 'Invoice link copied to clipboard!');
				ReactGA.event({
					category: 'Invoice',
					action: 'Copied Invoice Link',
					label: `Invoice: ${hashId}`,
				});
			}
		} catch (error) {
			console.error('Error sharing invoice:', error);
			// Fallback to clipboard if share fails
			try {
				await navigator.clipboard.writeText(shareUrl);
				notify('success', 'Invoice link copied to clipboard!');
			} catch (clipboardError) {
				notify('error', 'Failed to share invoice');
			}
		}
	};

	// Save the original favicon when component mounts
	useEffect(() => {
		const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
		setOriginalFavicon(favicon ? (favicon.cloneNode(true) as HTMLLinkElement) : null);
	}, []);

	useEffect(() => {
		const fetchInvoiceData = async () => {
			try {
				setLoading(true);
				const url = skipTracking
					? `/orders/invoice-data/${hashId}?skip_tracking=true`
					: `/orders/invoice-data/${hashId}`;

				const response = await apiClient.get(url);

				if (response.status === 'Success' && response.data) {
					setInvoiceData(response.data);

					// Set document title with shop name
					document.title = `Invoice from ${response.data.shop_name}  | VendReady`;

					// Set shop logo as favicon if available
					if (response.data.shop_logo) {
						try {
							// Remove existing favicon(s)
							const existingFavicons = document.querySelectorAll("link[rel*='icon']");
							existingFavicons.forEach((favicon) => {
								document.head.removeChild(favicon);
							});

							// Create new favicon link element
							const faviconLink = document.createElement('link');
							faviconLink.rel = 'icon';
							faviconLink.href = response.data.shop_logo;
							faviconLink.type = 'image/png'; // Assume most logos are in PNG format

							// Add the new favicon to the document head
							document.head.appendChild(faviconLink);
						} catch (error) {
							console.error('Error setting favicon:', error);
							// Continue even if favicon can't be set
						}
					}

					ReactGA.event({
						category: 'Invoice',
						action: 'Viewed Invoice',
						label: `Invoice: ${hashId}`,
					});
				} else {
					setError('Failed to load invoice data');
				}
			} catch (err) {
				console.error('Error fetching invoice data:', err);
				setError('Error loading invoice. Please try again later.');
				notify('error', 'Failed to load invoice data');
			} finally {
				setLoading(false);
			}
		};

		if (hashId) {
			fetchInvoiceData();
		}

		// Restore original title and favicon when component unmounts
		return () => {
			document.title = 'VendReady - Simplified Sales & Stock Management';

			// Restore original favicon
			try {
				if (originalFavicon) {
					// Remove current favicon(s)
					const existingFavicons = document.querySelectorAll("link[rel*='icon']");
					existingFavicons.forEach((favicon) => {
						document.head.removeChild(favicon);
					});

					// Add back the original favicon
					document.head.appendChild(originalFavicon);
				}
			} catch (error) {
				console.error('Error restoring favicon:', error);
			}
		};
	}, [hashId, skipTracking, originalFavicon]);

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error || !invoiceData) {
		return (
			<Box sx={{ p: 3, textAlign: 'center' }}>
				<Typography variant="h6" color="error">
					{error || 'Failed to load invoice'}
				</Typography>
			</Box>
		);
	}

	const {
		order,
		order_price_total,
		shop_name,
		invoice_date,
		invoice_number,
		payment_status,
		payment_instruction,
	} = invoiceData;

	// Calculate subtotal and tax
	const subtotal = order.variants.reduce(
		(sum, variant) => sum + variant.unit_price_at_purchase * variant.quantity,
		0
	);
	const totalDiscount = order.variants.reduce((sum, variant) => sum + variant.discount_amount * variant.quantity, 0);

	return (
		<>
			{/* Add print styles */}
			<style type="text/css" media="print">
				{`
                @page {
                    size: A4;
                    margin: 0.5cm;
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                @media print {
                    .no-print, .no-print * {
                        display: none !important;
                    }
                }
                `}
			</style>

			{/* Share button - only show when skipTracking is true */}
			{skipTracking && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						mb: 2,
						mt: 2,
					}}
					className="no-print"
				>
					<Button
						variant="contained"
						color="primary"
						startIcon={<ShareIcon />}
						onClick={handleShareInvoice}
						sx={{
							backgroundColor: '#2563eb',
							'&:hover': {
								backgroundColor: '#1d4ed8',
							},
							px: 3,
							py: 1,
						}}
					>
						Share Invoice
					</Button>
				</Box>
			)}

			<Box
				ref={invoiceRef}
				sx={{
					maxWidth: '800px',
					width: '95%',
					margin: { xs: '20px auto', sm: '40px auto' },
					backgroundColor: 'white',
					borderRadius: '8px',
					boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
					overflow: 'hidden',
				}}
			>
				{/* Header */}
				<Box
					sx={{
						backgroundColor: '#1e3a5f',
						color: 'white',
						padding: { xs: '20px', sm: '30px', md: '40px' },
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						justifyContent: 'space-between',
						alignItems: { xs: 'flex-start', sm: 'center' },
						gap: { xs: '20px', sm: '0' },
						position: 'relative',
					}}
				>
					<Box>
						{invoiceData.shop_logo ? (
							<Box
								sx={{
									maxWidth: { xs: '150px', sm: '180px', md: '200px' },
									mb: 2,
								}}
							>
								<img
									src={invoiceData.shop_logo}
									alt={shop_name}
									style={{
										maxWidth: '100%',
										maxHeight: '120px',
										padding: '15px',
										backgroundColor: 'white',
										objectFit: 'contain',
										borderRadius: '4px',
									}}
								/>
							</Box>
						) : (
							<Typography
								variant="h3"
								sx={{
									fontWeight: 700,
									mb: 2,
									fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									letterSpacing: '-0.02em',
								}}
							>
								{shop_name}
							</Typography>
						)}
						<Typography
							sx={{
								opacity: 0.9,
								mb: 1,
								fontFamily:
									'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								fontSize: '0.95rem',
							}}
						>
							{shop_name}
						</Typography>
					</Box>
					<Box
						sx={{
							textAlign: { xs: 'left', sm: 'right' },
							width: { xs: '100%', sm: 'auto' },
						}}
					>
						<Typography
							sx={{
								fontSize: { xs: '1.5rem', sm: '1.8rem' },
								fontWeight: 700,
								mb: 2,
								letterSpacing: '0.05em',
								fontFamily:
									'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							}}
						>
							INVOICE
						</Typography>
						<Typography
							sx={{
								mb: 1,
								fontSize: '0.95rem',
								fontFamily:
									'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							}}
						>
							<strong style={{ fontWeight: 600 }}>Invoice #:</strong> {invoice_number}
						</Typography>
						<Typography
							sx={{
								mb: 1,
								fontSize: '0.95rem',
								fontFamily:
									'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							}}
						>
							<strong style={{ fontWeight: 600 }}>Date:</strong> {invoice_date}
						</Typography>
						<Box
							sx={{
								display: 'inline-block',
								padding: '6px 12px',
								borderRadius: '20px',
								fontSize: '0.875rem',
								fontWeight: 600,
								backgroundColor: payment_status === 'Paid' ? '#dcfce7' : '#fef3c7',
								color: payment_status === 'Paid' ? '#166534' : '#92400e',
								mt: 2,
							}}
						>
							{payment_status}
						</Box>
					</Box>
				</Box>

				{/* Body */}
				<Box sx={{ padding: { xs: '20px', sm: '30px', md: '40px' } }}>
					{/* Billing Section */}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
							gap: { xs: '20px', sm: '40px' },
							mb: { xs: 3, sm: 5 },
						}}
					>
						<Box>
							<Typography
								sx={{
									color: '#1f2937',
									fontSize: { xs: '0.875rem', sm: '1rem' },
									fontWeight: 600,
									mb: 2,
									textTransform: 'uppercase',
									letterSpacing: '0.05em',
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								Bill To
							</Typography>
							<Typography
								sx={{
									fontWeight: 600,
									mb: 1,
									fontSize: '1rem',
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								{order.customer_name}
							</Typography>
							<Typography
								sx={{
									color: '#6b7280',
									fontSize: '0.875rem',
									lineHeight: 1.6,
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								{order.order_address || order.customer_address}
							</Typography>
							<Typography
								sx={{
									color: '#6b7280',
									fontSize: '0.875rem',
									lineHeight: 1.6,
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								{order.order_contact || order.customer_phone}
							</Typography>
							<Typography
								sx={{
									color: '#6b7280',
									fontSize: '0.875rem',
									lineHeight: 1.6,
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								}}
							>
								{order.customer_email}
							</Typography>
						</Box>
					</Box>

					{/* Variants Table - Desktop version */}
					<Box
						sx={{
							mb: { xs: 3, sm: 5 },
							display: { xs: 'none', md: 'block' },
						}}
					>
						<Box sx={{ overflow: 'hidden', borderRadius: '8px' }}>
							<Box
								sx={{
									backgroundColor: '#1f2937',
									color: 'white',
									padding: '15px 20px',
									display: 'grid',
									gridTemplateColumns: '2fr 1fr 1fr 1fr',
									fontWeight: 600,
									fontSize: { xs: '0.875rem', sm: '0.95rem' },
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									letterSpacing: '0.025em',
								}}
							>
								<Box>Item</Box>
								<Box sx={{ textAlign: 'center' }}>Quantity</Box>
								<Box sx={{ textAlign: 'center' }}>Unit Price</Box>
								<Box sx={{ textAlign: 'right' }}>Amount</Box>
							</Box>
							{order.variants.map((variant, index) => (
								<Box
									key={variant.id}
									sx={{
										display: 'grid',
										gridTemplateColumns: '2fr 1fr 1fr 1fr',
										padding: { xs: '15px', sm: '20px' },
										borderBottom: index < order.variants.length - 1 ? '1px solid #e5e7eb' : 'none',
										fontSize: { xs: '16px', sm: '18px' },
										'&:hover': {
											backgroundColor: '#f9fafb',
										},
									}}
								>
									<Box>
										<div
											style={{
												fontWeight: 600,
												fontSize: '0.9375rem',
												color: '#1f2937',
												fontFamily:
													'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
											}}
										>
											{variant.product?.name}
										</div>
										<div
											style={{
												fontSize: '0.875rem',
												color: '#6b7280',
												fontFamily:
													'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
											}}
										>
											{variant.name} @ {order.currency}
											{Number(
												variant.unit_price_at_purchase -
													(variant.addons
														? variant.addons.reduce(
																(sum, addon) =>
																	sum +
																	Number(addon.quantity) *
																		Number(addon.unit_price_at_purchase),
																0
														  )
														: 0)
											).toFixed(2)}
										</div>
										{variant.addons && variant.addons.length > 0 && (
											<div style={{ marginTop: '6px' }}>
												{variant.addons.map((addon, addonIndex) => (
													<div
														key={addonIndex}
														style={{
															fontSize: '0.8125rem',
															color: '#9ca3af',
															fontStyle: 'italic',
															fontFamily:
																'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
														}}
													>
														+ {addon.name} (x{addon.quantity}) @ {order.currency}
														{Number(addon.unit_price_at_purchase).toFixed(2)}
													</div>
												))}
											</div>
										)}
									</Box>
									<Box
										sx={{
											textAlign: 'center',
											alignSelf: 'center',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
											fontSize: '0.9375rem',
										}}
									>
										{variant.quantity}
									</Box>
									<Box
										sx={{
											textAlign: 'center',
											alignSelf: 'center',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
											fontSize: '0.9375rem',
										}}
									>
										<span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>
											{order.currency}
										</span>
										{Number(variant.unit_price_at_purchase).toFixed(2)}
									</Box>
									<Box
										sx={{
											textAlign: 'right',
											alignSelf: 'center',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
											fontSize: '0.9375rem',
										}}
									>
										<span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>
											{order.currency}
										</span>
										{Number(variant.total_price).toFixed(2)}
									</Box>
								</Box>
							))}
						</Box>
					</Box>

					{/* Variants Table - Mobile version */}
					<Box
						sx={{
							mb: { xs: 3, sm: 5 },
							display: { xs: 'block', md: 'none' },
						}}
					>
						{order.variants.map((variant, index) => (
							<Box
								key={variant.id}
								sx={{
									padding: '15px',
									borderRadius: '8px',
									border: '1px solid #e5e7eb',
									mb: 2,
									backgroundColor: '#f9fafb',
									fontSize: '16px',
								}}
							>
								<Typography
									sx={{
										fontWeight: 600,
										mb: 0.5,
										fontFamily:
											'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										color: '#1f2937',
									}}
								>
									{variant.product?.name}
								</Typography>
								<Typography
									sx={{
										fontSize: '0.875rem',
										color: '#6b7280',
										mb: 1,
										fontFamily:
											'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									}}
								>
									{variant.name} @ {order.currency}
									{Number(
										variant.unit_price_at_purchase -
											(variant.addons
												? variant.addons.reduce(
														(sum, addon) =>
															sum +
															Number(addon.quantity) *
																Number(addon.unit_price_at_purchase),
														0
												  )
												: 0)
									).toFixed(2)}
								</Typography>
								{variant.addons && variant.addons.length > 0 && (
									<Box sx={{ mb: 1 }}>
										{variant.addons.map((addon, addonIndex) => (
											<Typography
												key={addonIndex}
												sx={{
													fontSize: '0.8125rem',
													color: '#9ca3af',
													fontStyle: 'italic',
													fontFamily:
														'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
												}}
											>
												+ {addon.name} (x{addon.quantity}) @ {order.currency}
												{Number(addon.unit_price_at_purchase).toFixed(2)}
											</Typography>
										))}
									</Box>
								)}
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										mb: 1,
									}}
								>
									<Typography
										sx={{
											color: '#6b7280',
											fontSize: '0.875rem',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										}}
									>
										Quantity:
									</Typography>
									<Typography
										sx={{
											fontSize: '0.875rem',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										}}
									>
										{variant.quantity}
									</Typography>
								</Box>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										mb: 1,
									}}
								>
									<Typography
										sx={{
											color: '#6b7280',
											fontSize: '0.875rem',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										}}
									>
										Unit Price:
									</Typography>
									<Typography
										sx={{
											fontSize: '0.875rem',
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										}}
									>
										<span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>
											{order.currency}
										</span>
										{Number(variant.unit_price_at_purchase).toFixed(2)}
									</Typography>
								</Box>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										fontWeight: 'bold',
									}}
								>
									<Typography
										sx={{
											fontWeight: 600,
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										}}
									>
										Amount:
									</Typography>
									<Typography
										sx={{
											fontWeight: 600,
											fontFamily:
												'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
										}}
									>
										<span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>
											{order.currency}
										</span>
										{Number(variant.total_price).toFixed(2)}
									</Typography>
								</Box>
							</Box>
						))}
					</Box>

					{/* Totals Section */}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							mb: { xs: 3, sm: 5 },
						}}
					>
						<Box
							sx={{
								width: { xs: '100%', sm: '300px' },
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									padding: '10px 0',
									borderBottom: '1px solid #e5e7eb',
									fontSize: { xs: '16px', sm: '18px' },
								}}
							>
								<Typography
									sx={{
										color: '#6b7280',
										fontFamily:
											'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									}}
								>
									Subtotal:
								</Typography>
								<Typography
									sx={{
										fontFamily:
											'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									}}
								>
									{order.currency}
									{Number(subtotal).toFixed(2)}
								</Typography>
							</Box>
							{totalDiscount > 0 && (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										padding: '10px 0',
										borderBottom: '1px solid #e5e7eb',
										fontSize: { xs: '16px', sm: '18px' },
									}}
								>
									<Typography sx={{ color: '#666' }}>Discount:</Typography>
									<Typography>
										-{order.currency}
										{Number(totalDiscount).toFixed(2)}
									</Typography>
								</Box>
							)}
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									padding: '20px 0 10px',
									fontSize: { xs: '18px', sm: '20px' },
									fontWeight: 'bold',
									color: '#2563eb',
								}}
							>
								<Typography
									sx={{
										fontWeight: 700,
										fontFamily:
											'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									}}
								>
									Total Amount:
								</Typography>
								<Typography
									sx={{
										fontWeight: 700,
										fontFamily:
											'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									}}
								>
									{order.currency}
									{Number(order_price_total).toFixed(2)}
								</Typography>
							</Box>
						</Box>
					</Box>

					{/* Payment Instructions Section */}
					{payment_instruction && (
						<Box
							sx={{
								backgroundColor: '#f9fafb',
								padding: { xs: '20px', sm: '24px' },
								borderRadius: '12px',
								mb: { xs: 3, sm: 5 },
								border: '1px solid #e5e7eb',
							}}
						>
							<Typography
								sx={{
									color: '#1f2937',
									fontWeight: 600,
									fontSize: { xs: '0.875rem', sm: '1rem' },
									mb: 2,
									textTransform: 'uppercase',
									letterSpacing: '0.05em',
								}}
							>
								Payment Instructions
							</Typography>
							<Typography
								sx={{
									color: '#4b5563',
									fontSize: { xs: '0.875rem', sm: '0.9375rem' },
									lineHeight: 1.7,
									fontFamily:
										'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
									whiteSpace: 'pre-line', // Preserve line breaks
								}}
							>
								{payment_instruction}
							</Typography>
						</Box>
					)}
				</Box>

				{/* Footer */}
				<Box
					sx={{
						textAlign: 'center',
						padding: { xs: '15px', sm: '20px' },
						color: '#9ca3af',
						borderTop: '1px solid #e5e7eb',
					}}
				>
					<Typography
						sx={{
							fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							fontSize: '0.875rem',
						}}
					>
						Thank you for your business!
					</Typography>
					<Typography
						sx={{
							mt: 1,
							fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
							fontSize: '0.8125rem',
						}}
					>
						© 2025 {shop_name}. All rights reserved.
					</Typography>
				</Box>
			</Box>
			<Box sx={{ width: '95%', maxWidth: '800px', margin: '0 auto', mb: 4 }}>
				{/* Bottom Download Buttons */}
				{/* <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        gap: 2
                    }}
                    className="no-print"
                >
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CloudDownloadIcon />}
                        onClick={handleDownloadInvoice}
                        sx={{
                            backgroundColor: '#2563eb',
                            '&:hover': {
                                backgroundColor: '#1d4ed8',
                            },
                            px: 4,
                            py: 1
                        }}
                    >
                        Save Invoice as PDF
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<PrintIcon />}
                        onClick={handlePrintInvoice}
                        sx={{
                            px: 4,
                            py: 1
                        }}
                    >
                        Print Invoice
                    </Button>
                </Box> */}

				{!skipTracking && (
					<Box
						sx={{
							background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
							borderRadius: '12px',
							padding: { xs: '20px', sm: '25px 30px' },
							mt: 3,
							boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
						}}
						className="no-print"
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'column', sm: 'row' },
								alignItems: 'center',
								gap: { xs: 2, sm: 4 },
								color: 'white',
							}}
						>
							{/* Left side - Content */}
							<Box sx={{ flex: 1 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
									<Box
										sx={{
											backgroundColor: 'rgba(255, 255, 255, 0.2)',
											padding: '4px 8px',
											borderRadius: '12px',
											fontSize: '0.75rem',
											fontWeight: 'bold',
											color: 'white',
											border: '1px solid rgba(255, 255, 255, 0.3)',
										}}
									>
										✨ Powered by VendReady
									</Box>
								</Box>
								<Typography
									variant="h6"
									sx={{
										fontWeight: 'bold',
										mb: 0.5,
										fontSize: { xs: '1.1rem', sm: '1.25rem' },
									}}
								>
									Grow Your Business with{' '}
									<Box
										component="span"
										sx={{
											background: 'linear-gradient(to right, #ffffff, #e0e7ff)',
											backgroundClip: 'text',
											WebkitBackgroundClip: 'text',
											WebkitTextFillColor: 'transparent',
											fontSize: { xs: '1.1rem', sm: '1.25rem' },
											fontWeight: 800,
											letterSpacing: '-0.02em',
										}}
									>
										VendReady
									</Box>
								</Typography>
								<Typography
									sx={{
										fontSize: { xs: '0.875rem', sm: '0.95rem' },
										opacity: 0.9,
										lineHeight: 1.5,
									}}
								>
									Streamline your business with powerful inventory management, professional invoicing,
									and insightful sales analytics.
								</Typography>
							</Box>

							{/* Right side - CTA */}
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: { xs: 'stretch', sm: 'flex-end' },
									gap: 0.5,
									flexShrink: 0,
									width: { xs: '100%', sm: 'auto' },
								}}
							>
								<Button
									variant="contained"
									href={process.env.REACT_APP_WEBSITE_URL || 'https://vendready.store'}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => {
										ReactGA.event({
											category: 'Invoice',
											action: 'Clicked VendReady CTA',
											label: `From Invoice: ${hashId}`,
										});
									}}
									sx={{
										backgroundColor: 'white',
										color: '#667eea',
										'&:hover': {
											backgroundColor: '#f3f4f6',
										},
										px: 3,
										py: 1,
										fontSize: '0.95rem',
										fontWeight: 'bold',
										textTransform: 'none',
										borderRadius: '50px',
										whiteSpace: 'nowrap',
									}}
								>
									Start Free Trial
								</Button>
								<Typography
									sx={{
										fontSize: '0.75rem',
										opacity: 0.8,
										textAlign: { xs: 'center', sm: 'right' },
										whiteSpace: 'nowrap',
									}}
								>
									No credit card required
								</Typography>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</>
	);
};

export default InvoiceView;
